import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import * as authApi from '../../../lib/api/auth';
import * as hrAttendApi from '../../../lib/api/hrAttend';

const dismissedKey = (user, briefing) =>
  user?.usrUuid && briefing?.date
    ? `todayBriefingDismissed:${user.usrUuid}:${briefing.date}`
    : null;

const countTotal = (summary = {}) =>
  (summary.approvalCount || 0) +
  (summary.notificationCount || 0) +
  (summary.scheduleCount || 0) +
  (summary.leaveRequestCount || 0);

const TodayBriefingContainer = ({ compact = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [briefing, setBriefing] = useState(null);

  const key = useMemo(() => dismissedKey(user, briefing), [user, briefing]);
  const total = countTotal(briefing?.summary);

  const loadBootstrap = useCallback(
    async ({ autoOpen = false } = {}) => {
      if (!user?.usrUuid) return;

      setLoading(true);
      try {
        const res = await authApi.getTodayBootstrap();
        const info = res.data.info;
        setBriefing(info);

        const nextKey = dismissedKey(user, info);
        if (
          autoOpen &&
          !isMobile &&
          info?.briefingVisible &&
          nextKey &&
          localStorage.getItem(nextKey) !== 'Y'
        ) {
          setOpen(true);
        }
      } catch (e) {
        // 브리핑 실패가 업무 진입을 막으면 안 된다.
      } finally {
        setLoading(false);
      }
    },
    [isMobile, user],
  );

  useEffect(() => {
    loadBootstrap({ autoOpen: true });
  }, [loadBootstrap]);

  const handleOpen = () => {
    setOpen(true);
    loadBootstrap();
  };

  const handleClose = () => {
    if (key) {
      localStorage.setItem(key, 'Y');
    }
    setOpen(false);
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      await hrAttendApi.checkInToday();
      enqueueSnackbar('출근 처리되었습니다.', { variant: 'success' });
      await loadBootstrap();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          e?.response?.data?.userMessage ||
          '출근 처리에 실패했습니다.',
        { variant: 'error' },
      );
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    setCheckingOut(true);
    try {
      await hrAttendApi.checkOutToday(true);
      enqueueSnackbar('퇴근 처리되었습니다.', { variant: 'success' });
      await loadBootstrap();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          e?.response?.data?.userMessage ||
          '퇴근 처리에 실패했습니다.',
        { variant: 'error' },
      );
    } finally {
      setCheckingOut(false);
    }
  };

  const attendance = briefing?.attendance || {};
  const summary = briefing?.summary || {};
  // 출근 기록이 없는 상태를 따로 본다. 퇴근 여부만으로 갈라면 아직 출근도 안 한 사람이
  // "근무 중" 으로 보이고, 퇴근 버튼은 "18:00 이후 퇴근" 이라며 시간을 기다리라고 한다.
  // 못 누르는 진짜 이유는 출근을 안 한 것이다.
  const notCheckedIn = !attendance.attendUuid && !attendance.inTm;

  const attendStatusLabel = attendance.outTm
    ? '퇴근 완료'
    : notCheckedIn
      ? '미출근'
      : '근무 중';

  const checkInDisabled = checkingIn || loading;

  // 정시 전에도 누를 수 있게 연다. 서버는 수동 퇴근을 원래 허용하고(manual=true) 화면도
  // 그렇게 부르고 있었는데 버튼만 잠겨 있었다. 그래서 정시 전에 퇴근하면 기록을 남길 방법이
  // 없었고, 근태가 출근만 찍힌 채로 쌓였다. 라벨로 정시 전이라는 것만 알린다.
  const checkOutDisabled =
    checkingOut ||
    loading ||
    !attendance.attendUuid ||
    !!attendance.outTm;

  const checkOutLabel = attendance.outTm
    ? '퇴근 완료'
    : attendance.canCheckOut
      ? '퇴근 처리'
      : '정시 전 퇴근';

  return (
    <>
      <Tooltip title="오늘 브리핑">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          size={compact ? 'small' : 'medium'}
        >
          <Badge
            color="primary"
            badgeContent={total}
            invisible={!total}
            max={99}
          >
            <WbSunnyIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer anchor={isMobile ? 'bottom' : 'right'} open={open} onClose={handleClose}>
        <Box sx={{ width: { xs: '100vw', sm: 380 }, maxWidth: '100vw', p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <WbSunnyIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                오늘 브리핑
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {briefing?.date || '-'}
              </Typography>
            </Box>
            <Tooltip title="새로고침">
              <span>
                <IconButton size="small" onClick={() => loadBootstrap()} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {loading && !briefing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Box sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    color={attendance.outTm ? 'success' : notCheckedIn ? 'default' : 'primary'}
                    label={attendStatusLabel}
                  />
                  {attendance.autoCheckedIn && (
                    <Chip size="small" variant="outlined" label="자동 출근" />
                  )}
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      출근
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {attendance.inTm || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      퇴근
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {attendance.outTm || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      기준
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {attendance.regularOutTm || '18:00'}
                    </Typography>
                  </Box>
                </Stack>
                {notCheckedIn ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    startIcon={checkingIn ? <CircularProgress size={14} /> : <LoginIcon />}
                    disabled={checkInDisabled}
                    onClick={handleCheckIn}
                    sx={{ mt: 2 }}
                  >
                    출근 처리
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={checkingOut ? <CircularProgress size={14} /> : <LogoutIcon />}
                    disabled={checkOutDisabled}
                    onClick={handleCheckOut}
                    sx={{ mt: 2 }}
                  >
                    {checkOutLabel}
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <List dense disablePadding>
                {(briefing?.messages || []).map((message) => (
                  <ListItem key={message} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primary={message} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AssignmentTurnedInIcon fontSize="small" color="action" />
                  <Typography variant="body2">대기 결재 {summary.approvalCount || 0}건</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NotificationsIcon fontSize="small" color="action" />
                  <Typography variant="body2">읽지 않은 알림 {summary.notificationCount || 0}건</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EventAvailableIcon fontSize="small" color="action" />
                  <Typography variant="body2">오늘 일정 {summary.scheduleCount || 0}건</Typography>
                </Stack>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default TodayBriefingContainer;
